import React, { useLayoutEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AgGridReactProps } from 'ag-grid-react/lib/agGridReact';
import { GridApi, ColumnApi } from 'ag-grid-community';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-bootstrap.css';

interface IioDataGridProps extends Omit<AgGridReactProps, 'onGridReady' | 'onModelUpdated'> {
  // Explicit height overrides the auto fill-to-bottom behavior.
  height?: string | number;
  onGridReady?: (api: GridApi, columnApi: ColumnApi) => void;
  // By default the grid stretches its columns to fill the width (sizeColumnsToFit),
  // which suits a small, fixed column set. Set this for a wide many-column grid where
  // columns should keep their own widths and the grid scrolls horizontally instead —
  // otherwise fit-to-width crushes them and overrides user/restored column widths.
  suppressAutoSizeColumns?: boolean;
}

// Gap left below the grid so it doesn't touch the viewport edge / trigger page scroll.
const BOTTOM_GAP = 20;
const MIN_HEIGHT = 200;

export function IioDataGrid({
  height,
  onGridReady,
  defaultColDef,
  suppressAutoSizeColumns = false,
  ...rest
}: IioDataGridProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [autoHeight, setAutoHeight] = useState<number>();

  // When no explicit height is given, size the grid to fill from its own
  // top offset down to the bottom of the viewport. This avoids a hardcoded
  // chrome guess (which differs per page) and the resulting nested scrollbars.
  useLayoutEffect(() => {
    if (height != null) return;
    const el = wrapperRef.current;
    if (!el) return;
    const recompute = () => {
      const top = el.getBoundingClientRect().top;
      setAutoHeight(Math.max(MIN_HEIGHT, window.innerHeight - top - BOTTOM_GAP));
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [height]);

  const resolvedHeight = height ?? autoHeight ?? `calc(100vh - 300px)`;

  return (
    <div ref={wrapperRef} className="ag-theme-bootstrap" style={{ height: resolvedHeight, marginTop: 20 }}>
      <AgGridReact
        rowHeight={48}
        sortingOrder={['desc', 'asc', null]}
        defaultColDef={{ sortable: true, resizable: true, cellClass: 'alternating-rows', ...defaultColDef }}
        rowModelType="infinite"
        suppressContextMenu
        cacheBlockSize={100}
        infiniteInitialRowCount={20}
        onGridReady={({ api, columnApi }) => onGridReady?.(api, columnApi)}
        onGridSizeChanged={({ api, clientWidth }) => { if (!suppressAutoSizeColumns && clientWidth > 0) api.sizeColumnsToFit(); }}
        onModelUpdated={({ api }) => { if (!suppressAutoSizeColumns) api.sizeColumnsToFit(); }}
        {...rest}
      />
    </div>
  );
}
